import { useTranslation } from "react-i18next";
import { usePageSEO } from "@/lib/use-page-seo";
import { useLocalePath } from "@/lib/use-locale-path";
import WCLogo from "@/components/WCLogo";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import MegaFooter from "@/components/MegaFooter";
import { WC2026_TEAMS } from "../../../server/teams";
import { TEAM_ROUTES, slugifyTeam, teamPath, groupPath } from "../../../shared/seo-routes";
import {
  teamGroupStageFixtures,
  formatFixtureDateTime,
} from "@/lib/fixtures-for-seo";
import type { SupportedLanguage } from "@/i18n";

interface Props {
  params: { teamSlug?: string };
}

export default function TeamPage({ params }: Props) {
  const { t, i18n } = useTranslation();
  const lp = useLocalePath();
  const slug = params.teamSlug ?? "";

  const route = TEAM_ROUTES.find((r) => r.slug === slug);
  const team = route ? WC2026_TEAMS.find((tm) => tm.code === route.code) : undefined;
  const availableLanguages: SupportedLanguage[] = team
    ? ["en", ...(route?.localizedLocales ?? [])]
    : ["en"];

  usePageSEO("seo.team.title", teamPath(slug));

  if (!team) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background text-foreground">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-2">{t("not_found.heading")}</h1>
          <a href={lp("/teams")} className="text-primary hover:underline">{t("seo.team.index_h1")}</a>
        </div>
      </div>
    );
  }

  const teammates = WC2026_TEAMS.filter(
    (tm) => tm.group === team.group && tm.code !== team.code,
  );

  const lang = (i18n.language || "en").slice(0, 2);
  const fixtures = teamGroupStageFixtures(team.code);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="border-b border-border/40 bg-background/80">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 flex items-center justify-between h-14">
          <WCLogo />
          <div className="flex items-center gap-4">
            <a href={lp("/")} className="text-sm text-muted-foreground hover:text-foreground">
              {t("nav.back")}
            </a>
            <LanguageSwitcher availableLanguages={availableLanguages} />
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-12">
        <nav aria-label="Breadcrumb" className="text-xs text-muted-foreground mb-6">
          <a href={lp("/")} className="hover:underline">{t("seo.common.home")}</a>
          <span className="mx-2">/</span>
          <a href={lp("/teams")} className="hover:underline">{t("seo.team.index_h1")}</a>
          <span className="mx-2">/</span>
          <span className="text-foreground">{team.name}</span>
        </nav>

        <div className="flex items-start gap-4 mb-6">
          <span className="text-5xl sm:text-6xl" aria-hidden="true">{team.flag}</span>
          <div>
            <h1 className="text-4xl sm:text-5xl font-extrabold uppercase tracking-tight"
                style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
              {t("seo.team.h1", { team: team.name })}
            </h1>
            <p className="mt-2 flex flex-wrap gap-3 text-xs uppercase tracking-wide text-muted-foreground">
              <span>{t("seo.team.group_label", { group: team.group })}</span>
              <span>·</span>
              <span>{team.confederation}</span>
            </p>
          </div>
        </div>

        <p className="text-base sm:text-lg text-muted-foreground max-w-2xl mb-4">
          {t("seo.team.intro", { team: team.name })}
        </p>

        <p className="text-sm text-muted-foreground max-w-2xl mb-8">
          {t("seo.team.detail_body", {
            team: team.name,
            confederation: team.confederation,
            group: team.group,
          })}
        </p>

        <div className="flex flex-wrap gap-3 mb-10">
          <a href={lp("/")}
             className="inline-flex items-center px-5 py-2.5 rounded-md bg-primary text-primary-foreground font-medium text-sm hover:opacity-90">
            {t("seo.team.cta", { team: team.name })}
          </a>
          <a href={lp(groupPath(team.group.toLowerCase()))}
             className="inline-flex items-center px-5 py-2.5 rounded-md border border-border text-sm hover:bg-muted/30">
            {t("seo.team.see_group", { group: team.group })}
          </a>
        </div>

        <section className="mb-10">
          <h2 className="text-2xl font-bold uppercase mb-4"
              style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
            {t("seo.team.fixtures_h2", { team: team.name })}
          </h2>
          <p className="text-sm text-muted-foreground mb-4">
            {t("seo.team.fixtures_intro", { team: team.name })}
          </p>
          {fixtures.length > 0 ? (
            <ul className="space-y-2">
              {fixtures.map((f) => (
                <li key={f.id}
                    className="rounded-md border border-border/50 px-4 py-3 text-sm flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{f.homeTeam.name}</span>
                    <span aria-hidden="true" className="text-base">{f.homeTeam.flag}</span>
                    <span className="text-muted-foreground">vs</span>
                    <span aria-hidden="true" className="text-base">{f.awayTeam.flag}</span>
                    <span className="font-medium">{f.awayTeam.name}</span>
                  </div>
                  <div className="text-xs text-muted-foreground flex flex-wrap gap-x-2 gap-y-1">
                    <span>{formatFixtureDateTime(lang, f.kickoffIso)}</span>
                    <span>·</span>
                    <span>{f.venue}, {f.city}</span>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-muted-foreground">{t("seo.fixtures.tbd")}</p>
          )}
        </section>

        <section className="mb-10">
          <h2 className="text-2xl font-bold uppercase mb-4"
              style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
            {t("seo.team.host_cities_h2", { team: team.name })}
          </h2>
          <p className="text-sm text-muted-foreground">
            {t("seo.team.host_cities_body")}
          </p>
        </section>

        <section className="mb-10">
          <h2 className="text-2xl font-bold uppercase mb-4"
              style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
            {t("seo.team.group_section", { group: team.group })}
          </h2>
          <p className="text-sm text-muted-foreground mb-4">
            {t("seo.team.group_section_body", { team: team.name, group: team.group })}
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {teammates.map((tm) => (
              <a key={tm.code}
                 href={lp(teamPath(slugifyTeam(tm.name)))}
                 className="flex items-center gap-3 rounded-md border border-border/50 px-3 py-2.5 hover:bg-muted/30">
                <span className="text-2xl" aria-hidden="true">{tm.flag}</span>
                <span className="flex-1 min-w-0">
                  <span className="block text-sm font-medium truncate">{tm.name}</span>
                  <span className="block text-[11px] text-muted-foreground">{tm.confederation}</span>
                </span>
              </a>
            ))}
          </div>
        </section>

        <section className="mb-10">
          <h2 className="text-2xl font-bold uppercase mb-4"
              style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
            {t("seo.common.works_with")}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
            <a href={lp("/platforms/apple-calendar-world-cup-fixtures")}
               className="block rounded-md border border-border/50 px-4 py-3 hover:bg-muted/30">
              <div className="font-medium">{t("seo.platform.apple.name")}</div>
              <div className="text-xs text-muted-foreground mt-1">iOS, iPadOS, macOS</div>
            </a>
            <a href={lp("/platforms/google-calendar-world-cup-fixtures")}
               className="block rounded-md border border-border/50 px-4 py-3 hover:bg-muted/30">
              <div className="font-medium">{t("seo.platform.google.name")}</div>
              <div className="text-xs text-muted-foreground mt-1">{t("seo.common.one_tap")}</div>
            </a>
            <a href={lp("/platforms/outlook-world-cup-fixtures")}
               className="block rounded-md border border-border/50 px-4 py-3 hover:bg-muted/30">
              <div className="font-medium">{t("seo.platform.outlook.name")}</div>
              <div className="text-xs text-muted-foreground mt-1">Web, Windows, Android</div>
            </a>
          </div>
        </section>
      </main>

      <MegaFooter />
    </div>
  );
}
